import { getLogger } from "@expert/logging";
import { Container, Flex, Group, Stack } from "@mantine/core";
import { useCallback, useEffect, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { CustomerPanel } from "@/customer-panel";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { ConfettiAnimation } from "@/sales-tools/reactions/Confetti";
import { getSessionByTaskId } from "@/sdk";
import { type ChatTask, useChatStore } from "@/sdk/agent/chat";
import { useWrapupTimer } from "@/sdk/sessions/hooks";
import { useSessionStore } from "@/sdk/sessions/session.store";
import { RenderError, useFeatureFlag } from "@/shared-utils";
import { useSetupChatGaiaSubscriptions } from "@/timeline/state";
import type { ChatSalesFeatureFlagConfig } from "../chat";
import { ActiveChatView } from "./ActiveChatView";
import { ChatQueue } from "./ChatQueue";
import styles from "./chat-components.module.css";
import { NoActiveChatView } from "./NoActiveChatView";

const logger = getLogger({
    rootModule: "ChatManager",
    supportTeam: "messaging",
});

export function ChatManager({ chatTasks }: { chatTasks: ChatTask[] }) {
    const { groupingChatQueueEnabled: isGroupChatsByPartner } = usePartnerFeatureConfigured();
    const activeChatId = useChatStore((state) => state.activeChatId);
    const setActiveChatId = useChatStore((state) => state.setActiveChatId);
    const clearActiveChatId = useChatStore((state) => state.clearActiveChatId);
    const { scheduleWrappingForTasks } = useWrapupTimer();

    /** activeChatTask memo derived directly from activeChatId and chatTasks */
    const activeChatTask = useMemo(
        () => chatTasks.find((task) => task.chatId === activeChatId),
        [activeChatId, chatTasks],
    );

    // Clear the activeChatId when no matching task is found
    useEffect(() => {
        if (activeChatId && !activeChatTask) {
            clearActiveChatId();
        }
    }, [activeChatId, activeChatTask, clearActiveChatId]);

    const onChatSelected = useCallback(
        (chat: ChatTask) => {
            const session = getSessionByTaskId(chat.id);
            if (session) {
                useSessionStore.getState().setSelectedSession(session.id);
            }
            setActiveChatId(chat.chatId);
        },
        [setActiveChatId],
    );

    const isSelectedChat = useCallback(({ chatId }: ChatTask) => activeChatId === chatId, [activeChatId]);

    useEffect(() => {
        scheduleWrappingForTasks(chatTasks);
    }, [chatTasks, scheduleWrappingForTasks]);

    // TODO: when flex is migrated make a new feature flag for chat sales config instead of sharing
    const { data: chatQueueSalesConfig = { isSalesEnabled: false } } = useFeatureFlag<ChatSalesFeatureFlagConfig>(
        "Twilio-Messaging-Sales-Config",
        {
            partner: activeChatTask?.partner,
            queue: activeChatTask?.routingQueue,
        },
    );

    const shouldShowSalesPanel =
        activeChatTask && activeChatTask.status !== "pending" && chatQueueSalesConfig.isSalesEnabled;

    useSetupChatGaiaSubscriptions(logger);

    return (
        <>
            <Group className={styles.chat_manager_group} flex={1} align="stretch" justify="stretch" wrap="nowrap">
                <ChatQueue
                    chats={chatTasks}
                    isGroupChatsByPartner={isGroupChatsByPartner}
                    isSelectedChat={isSelectedChat}
                    onChatSelected={onChatSelected}
                />
                <Stack
                    className={styles.chat_manager_stack}
                    mah="100%"
                    id="chat-view"
                    flex={1}
                    justify="stretch"
                    align="center"
                >
                    <Container className={styles.animationContainer} key={activeChatId}>
                        <ConfettiAnimation />
                    </Container>
                    {activeChatTask ? <ActiveChatView activeChatTask={activeChatTask} /> : <NoActiveChatView />}
                </Stack>
                {shouldShowSalesPanel && (
                    <Flex className={styles.chat_manager_customer_panel}>
                        <ErrorBoundary fallback={<RenderError />}>
                            <CustomerPanel />
                        </ErrorBoundary>
                    </Flex>
                )}
            </Group>
        </>
    );
}
